import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../assets/styles/styles.scss";
import Metadata from "../../components/Metadata";
import Header from "../../components/dashboard/Header";
import Menu from "../../components/dashboard/Menu";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../config/firebase";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Spin } from "antd";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

export default function PublicAppDashboard() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    medicationUsers: { total: 0, approved: 0 },
    pharmacies: { total: 0, requested: 0 },
    drivers: { total: 0, requested: 0 },
    orders: { total: 0, delivered: 0, pending: 0 },
  });

  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      const patientsSnap = await getDocs(
        query(collection(db, "medicationUsers"), where("profileType", "==", "patient"))
      );
      const patientsData = patientsSnap.docs.map((d) => d.data());
      const approvedPatients = patientsData.filter((p) => p.status === "approved").length;

      const pharmaciesSnap = await getDocs(
        query(collection(db, "medicationUsers"), where("profileType", "==", "pharmacy"))
      );
      const requestedPharmaciesSnap = await getDocs(
        query(
          collection(db, "medicationUsers"),
          where("profileType", "==", "pharmacy"),
          where("status", "==", "requested")
        )
      );

      const driversSnap = await getDocs(
        query(collection(db, "medicationUsers"), where("profileType", "==", "driver"))
      );
      const requestedDriversSnap = await getDocs(
        query(
          collection(db, "medicationUsers"),
          where("profileType", "==", "driver"),
          where("status", "==", "requested")
        )
      );

      const ordersSnap = await getDocs(collection(db, "medicationOrders"));
      const ordersData = ordersSnap.docs.map((d) => d.data());
      const deliveredOrders = ordersData.filter((o) => o.status === "delivered").length;
      const pendingOrders = ordersData.length - deliveredOrders;

      setSummary({
        medicationUsers: { total: patientsData.length, approved: approvedPatients },
        pharmacies: { total: pharmaciesSnap.size, requested: requestedPharmaciesSnap.size },
        drivers: { total: driversSnap.size, requested: requestedDriversSnap.size },
        orders: { total: ordersData.length, delivered: deliveredOrders, pending: pendingOrders },
      });

      setLoading(false);
    }

    fetchData();
  }, []);

  const renderDoughnut = (data1, data2, label1, label2) => ({
    labels: [label1, label2],
    datasets: [
      {
        label: "Proportion",
        data: [data1, data2],
        backgroundColor: ["#6dbe42", "#FF4747"],
        hoverOffset: 4,
      },
    ],
  });

  const cardStyle = {
    padding: "50px",
    borderRadius: "10px",
    backgroundColor: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    textAlign: "center",
  };

  const buttonStyle = {
    marginTop: "15px",
    padding: "15px",
    borderRadius: "5px",
    border: "none",
    backgroundColor: "#6dbe42",
    color: "#fff",
    cursor: "pointer",
    fontSize: "1rem"
  };

  return (
    <>
      <h2 className="dashboard-title" style={{ marginBottom: 30 }}>
        Tableau de bord
      </h2>
      {loading ? (
        <Spin size="large" />
      ) : (
        <div
          className="dashboard-summary"
          style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}
        >
          <div className="summary-card" style={cardStyle}>
            <h3>Utilisateurs</h3>
            <Doughnut
              data={renderDoughnut(
                summary.medicationUsers.approved,
                summary.medicationUsers.total - summary.medicationUsers.approved,
                "Approuvés",
                "Non approuvés"
              )}
            />
            <p>Total: {summary.medicationUsers.total}</p>
            <p>Approuvés: {summary.medicationUsers.approved}</p>
            <button style={buttonStyle} onClick={() => navigate("/admin/public-app-users", {state: { category: 'patients' }})}>
              Voir les utilisateurs
            </button>
          </div>

          <div className="summary-card" style={cardStyle}>
            <h3>Pharmacies</h3>
            <Doughnut
              data={renderDoughnut(
                summary.pharmacies.total - summary.pharmacies.requested,
                summary.pharmacies.requested,
                "Validées",
                "En attente"
              )}
            />
            <p>Total: {summary.pharmacies.total}</p>
            <p>En attente: {summary.pharmacies.requested}</p>
            <button style={buttonStyle} onClick={() => navigate("/admin/public-app-users", {state: { category: 'pharmacies' }})}>
              Voir les pharmacies
            </button>
          </div>

          <div className="summary-card" style={cardStyle}>
            <h3>Livreurs</h3>
            <Doughnut
              data={renderDoughnut(
                summary.drivers.total - summary.drivers.requested,
                summary.drivers.requested,
                "Validés",
                "En attente"
              )}
            />
            <p>Total: {summary.drivers.total}</p>
            <p>En attente: {summary.drivers.requested}</p>
            <button style={buttonStyle} onClick={() => navigate("/admin/public-app-users", {state: { category: 'drivers' }})}>
              Voir les livreurs
            </button>
          </div>

          <div className="summary-card" style={cardStyle}>
            <h3>Commandes</h3>
            <Doughnut
              data={renderDoughnut(summary.orders.delivered, summary.orders.pending, "Livrées", "En attente")}
            />
            <p>Total: {summary.orders.total}</p>
            <p>Livrées: {summary.orders.delivered} . En attente: {summary.orders.pending}</p>
            <button style={buttonStyle} onClick={() => navigate("/admin/public-app-orders")}>
              Voir les commandes
            </button>
          </div>
        </div>
      )}
    </>
  );
}
